import { useState } from 'react';
import type { ReactElement } from 'react';
import { Button, SegmentedControl, cn } from '@xdocs/ui';
import { useFtueContext } from '../../../features/ftue/index.js';
import { PushSettingsSection } from '../../../features/push/index.js';
import { NotificationsPrefsSection } from '../NotificationsPrefsSection.js';
import { SettingsNav } from '../SettingsNav.js';
import { useNotificationsSettingsPageT } from './useNotificationsSettingsPageT.js';
import styles from './NotificationsSettingsPage.module.css';

type Cadence = 'instant' | 'digest';

/**
 * `/settings/notifications` (F33 "Notifications preferences (part of
 * F33/F23)": per-channel/type toggles + email cadence). The per-type ×
 * per-channel grid is the EXISTING `<NotificationsPrefsSection>`
 * (`../NotificationsPrefsSection.tsx`, WS-SETTINGS) — reused as-is, not
 * duplicated, per this task's "extend, don't rewrite" ownership rule; today
 * it also renders inline on `/settings` (`SettingsPage.tsx`) — this route
 * is the dedicated landing page FEATURES.md's Area I routes list calls for
 * ("prefs in `/settings/notifications`").
 *
 * Email cadence is a UI-only PLACEHOLDER (FEATURES.md: "Email cadence
 * (instant vs digest)"): no `cadence` field exists anywhere in
 * `notificationPref`/`users`/a dedicated table yet (this task's server
 * ownership is scoped to `apps/server/src/ftue/**` only — inventing new
 * settings/notifications storage is out of scope, see this task's returned
 * open-issues list). The control is fully interactive (so it doesn't read
 * as a broken/disabled affordance) but local-state-only and clearly labeled
 * "coming soon" — it does not yet round-trip to the server.
 */
export function NotificationsSettingsPage(): ReactElement {
  const { t } = useNotificationsSettingsPageT();
  const { replay } = useFtueContext();
  const [cadence, setCadence] = useState<Cadence>('instant');

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('notificationsSettingsPage:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('notificationsSettingsPage:page.description')}</p>
      </div>

      <SettingsNav />

      <NotificationsPrefsSection />

      <PushSettingsSection />

      <section className={cn(styles['card'])}>
        <h2 className={cn(styles['cardTitle'])}>{t('notificationsSettingsPage:cadence.title')}</h2>
        <p className={cn(styles['cardDescription'])}>{t('notificationsSettingsPage:cadence.description')}</p>
        <SegmentedControl
          label={t('notificationsSettingsPage:cadence.title')}
          value={cadence}
          onChange={(value) => setCadence(value)}
          options={[
            { value: 'instant', label: t('notificationsSettingsPage:cadence.instant') },
            { value: 'digest', label: t('notificationsSettingsPage:cadence.digest') },
          ]}
        />
        <p className={cn(styles['comingSoon'])}>{t('notificationsSettingsPage:cadence.comingSoon')}</p>
      </section>

      <section className={cn(styles['card'])}>
        <h2 className={cn(styles['cardTitle'])}>{t('notificationsSettingsPage:replay.title')}</h2>
        <div className={cn(styles['cardRow'])}>
          <p className={cn(styles['cardDescription'])}>{t('notificationsSettingsPage:replay.description')}</p>
          <Button variant="secondary" onClick={replay}>
            {t('notificationsSettingsPage:replay.action')}
          </Button>
        </div>
      </section>
    </div>
  );
}
